import { FC, useState, useCallback } from 'react';
import { Product } from '~/types/product';
import { Image } from '~/components/wrappers/Image';
import { ProductPrice } from '~/components/Price';
import { Routes } from '~/common/constants/routes';
import { shopifySrcSet } from '~/common/utils/image';
import {
  MatchWithText,
  MatchWithGrid,
  MatchWithCardDesktop,
  MatchWithCardImageWrapper,
  MatchWithCarouselWrapper,
  MatchWithCarouselViewport,
  MatchWithCarouselContainer,
  MatchWithCarouselSlide,
  MatchWithCardMobile,
  MatchWithCardMobileImageWrapper,
  MatchWithCardMobileText,
  MatchWithCardMobileTitle,
  MatchWithCardMobilePriceRow,
} from '../styles';
import CurrencyIcon from '~/components/CurrencyIcon';
import styled from 'styled-components';
import useMediaQuery from '~/common/hooks/useMediaQuery';
import {
  GlowInTheDarkBadge,
  hasGlowInTheDarkTag,
} from '~/components/GlowInTheDarkBadge';
import { trackSelectItem } from '~/common/utils/tracking';
import { TRACKING_LIST_IDS } from '~/common/constants/tracking';

interface MatchWithContentProps {
  matching: Product[];
}

const IMG_PARAMS = '&width=800&height=800&format=auto&quality=100';

const CurrencyIconStyled = styled(CurrencyIcon)`
  min-width: 0.625rem;
  height: 0.75rem;

  @media ${({ theme }) => theme.breakpoints.sm} {
    min-width: 0.5rem;
    height: 0.625rem;
  }
`;

const MatchWithCardDesktopItem: FC<{ product: Product }> = ({ product }) => {
  const frontImage = product.images[0];
  const backImage = product.images[1] ?? product.images[0];
  const [image, setImage] = useState(frontImage);
  const { isTablet } = useMediaQuery();

  const handleImageHover = useCallback(
    (showFirst = true) => {
      setImage(showFirst ? frontImage : backImage);
    },
    [frontImage, backImage],
  );

  const handleClick = () =>
    trackSelectItem(product, TRACKING_LIST_IDS.pdpMatchWith);

  return (
    <MatchWithCardDesktop
      to={`${Routes.Product}/${product.handle}`}
      onClick={handleClick}
    >
      <MatchWithCardImageWrapper
        onMouseEnter={() => (!isTablet ? handleImageHover(false) : null)}
        onMouseLeave={() => (!isTablet ? handleImageHover() : null)}
      >
        {image && (
          <Image
            src={`${image}${IMG_PARAMS}`}
            srcSet={shopifySrcSet(image)}
            sizes="25vw"
            alt={`${product.title} - Broken Planet`}
            loading="lazy"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        )}
      </MatchWithCardImageWrapper>
      {hasGlowInTheDarkTag(product.tags) && <GlowInTheDarkBadge compact />}
    </MatchWithCardDesktop>
  );
};

const MatchWithCardMobileItem: FC<{ product: Product }> = ({ product }) => {
  const image = product.images[0];

  const handleClick = () =>
    trackSelectItem(product, TRACKING_LIST_IDS.pdpMatchWith);

  return (
    <MatchWithCardMobile
      to={`${Routes.Product}/${product.handle}`}
      onClick={handleClick}
    >
      <MatchWithCardMobileImageWrapper>
        {image && (
          <Image
            src={`${image}${IMG_PARAMS}`}
            srcSet={shopifySrcSet(image)}
            sizes="50vw"
            alt={`${product.title} - Broken Planet`}
            loading="lazy"
          />
        )}
      </MatchWithCardMobileImageWrapper>
      <MatchWithCardMobileText>
        {hasGlowInTheDarkTag(product.tags) && <GlowInTheDarkBadge />}
        <MatchWithCardMobileTitle>{product.title}</MatchWithCardMobileTitle>
        <MatchWithCardMobilePriceRow>
          {Number(product.prices.maxPrice) > 0 && (
            <CurrencyIconStyled
              currencyCode={product.prices.currencyCode}
              variant="white"
            />
          )}
          <ProductPrice product={product} />
        </MatchWithCardMobilePriceRow>
      </MatchWithCardMobileText>
    </MatchWithCardMobile>
  );
};

export const MatchWithContent: FC<MatchWithContentProps> = ({ matching }) => {
  if (matching.length === 0) return null;

  return (
    <>
      <MatchWithText>MATCH WITH</MatchWithText>

      <MatchWithGrid>
        {matching.map((product) => (
          <MatchWithCardDesktopItem key={product.id} product={product} />
        ))}
      </MatchWithGrid>

      <MatchWithCarouselWrapper>
        <MatchWithCarouselViewport>
          <MatchWithCarouselContainer>
            {matching.map((product) => (
              <MatchWithCarouselSlide key={product.id}>
                <MatchWithCardMobileItem product={product} />
              </MatchWithCarouselSlide>
            ))}
          </MatchWithCarouselContainer>
        </MatchWithCarouselViewport>
      </MatchWithCarouselWrapper>
    </>
  );
};
